import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_auto-parts-shop-32/artifacts/j4kogefz_download.png";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 to-background"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <img 
              src={LOGO_URL} 
              alt="Brendolly's Auto Parts" 
              className="h-32 md:h-40 mx-auto mb-8"
            />
            <span className="text-primary font-medium uppercase tracking-wider">Our Story</span>
            <h1 className="font-heading text-5xl md:text-7xl font-black text-white uppercase tracking-tighter mt-4" data-testid="page-title">
              Born From<br />
              <span className="text-primary">Change</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Story Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-8 text-lg leading-relaxed">
              <p className="text-zinc-300 text-xl font-medium italic border-l-4 border-primary pl-6">
                Brendolly's Auto Parts was not born from comfort.<br />
                It was born from change.
              </p>

              <p className="text-zinc-400">
                In just one year, everything in my life transformed. What once was uncertainty became purpose. What once felt impossible turned into action. Brendolly's Auto Parts is the result of resilience, faith, and the courage to rebuild from the ground up.
              </p>

              <div className="py-8 border-y border-zinc-800">
                <p className="text-white text-2xl font-heading uppercase tracking-tight">
                  This is not just a story about car parts.<br />
                  <span className="text-primary">It's a story about people.</span>
                </p>
              </div>

              <p className="text-zinc-400">
                Cars connect lives — they take us to work, bring us back home, carry our families, our dreams, and our daily battles. We understood early on that behind every vehicle, there is a person depending on reliability, honesty, and trust. That's where we come in.
              </p>

              <p className="text-zinc-400">
                Brendolly's Auto Parts was created to serve with integrity, transparency, and respect. Whether you are a daily driver, a car enthusiast, or a professional mechanic, we treat every customer like a partner, not just a transaction.
              </p>

              <div className="bg-zinc-900 border border-zinc-800 p-8 my-12">
                <div className="space-y-4">
                  <p className="text-white">
                    <span className="text-primary font-bold">We believe</span> that quality parts build more than cars — they build confidence.
                  </p>
                  <p className="text-white">
                    <span className="text-primary font-bold">We believe</span> that service is not about selling, but about helping.
                  </p>
                  <p className="text-white">
                    <span className="text-primary font-bold">And we believe</span> that real success comes from genuine human connection.
                  </p>
                </div>
              </div>

              <p className="text-zinc-400">
                Every product we offer, every brand we carry, and every system we build is designed with one goal in mind: to make your journey safer, easier, and better.
              </p>

              <p className="text-zinc-400">
                This company represents growth, discipline, and a relentless pursuit of excellence — not only in automotive parts, but in how we treat people.
              </p>

              <div className="pt-8">
                <p className="text-white text-xl font-medium">
                  Brendolly's Auto Parts is proof that in one year, a life can change.
                </p>
                <p className="text-primary text-2xl font-heading uppercase tracking-tight mt-2">
                  And this is only the beginning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            Ready to Experience the Difference?
          </h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            Join thousands of satisfied customers who trust Brendolly's for their auto parts needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/shop">
              <Button className="bg-white text-primary hover:bg-white/90 font-bold uppercase tracking-wider px-8 py-3">
                Browse Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 font-bold uppercase tracking-wider px-8 py-3">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
